import { Build } from '@todesktop/shared';
import { useEffect, useState } from 'react';
import ErrorBoundary from '../../components/ErrorBoundary';
import LoginHOC from '../../components/LoginHOC';
import CliError from '../../utilities/CliError';
import getBuildAttributes from '../../utilities/getBuildAttributes';
import logger from '../../utilities/logger';
import { useAnalyticsCommand } from '../../utilities/useAnalytics';
import useExit from '../../utilities/useExit';
import BuildPicker from '../../components/BuildPicker';
import Cancellation from './components/Cancellation';
import SmokeTestView from './components/SmokeTestView';
import {
  isBuildTestable,
  isTestRunning,
  TotalTestProgress,
  validateBuild,
} from './utilities/build';
import cancelSmokeTest from './utilities/cancelSmokeTest';
import queueSmokeTest from './utilities/queueSmokeTest';
import SmokeState from './utilities/SmokeState';

import waitUntilFinished from './utilities/waitUntilFinished';

export default function SmokeTestCommand({
  buildId,
  configPath,
}: {
  buildId?: string;
  configPath?: string;
}) {
  useAnalyticsCommand('smoke-test', { buildId, configPath });

  const command = `todesktop smoke-test ${
    buildId === 'latest' ? '--latest' : '<id>'
  }`;

  return (
    <ErrorBoundary>
      <LoginHOC>
        <BuildPicker
          buildFilter={({ build }) => isBuildTestable(build)}
          buildId={buildId}
          commandUsed={command}
          configPath={configPath}
          noBuildsDescription="(candidates for smoke-test must be built with @todesktop/runtime@1.2.3-0 or later)"
        >
          {(id) => (
            <SmokeTestContainer
              commandUsed={command}
              buildId={id}
              configPath={configPath}
            />
          )}
        </BuildPicker>
      </LoginHOC>
    </ErrorBoundary>
  );
}

function SmokeTestContainer({
  buildId,
  commandUsed,
  configPath,
}: {
  buildId: string;
  commandUsed: string;
  configPath?: string;
}) {
  const exit = useExit();
  const [state, setState] = useState<SmokeState>({
    buildId,
    state: 'loading',
  });
  const [abortController] = useState(new AbortController());
  const abortSignal = abortController.signal;

  useEffect(() => {
    smokeTestWorkflow({ abortSignal, buildId, configPath, updateState }).catch(
      logger.error,
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function updateState(changes: Partial<SmokeState>) {
    setState((previousState) => ({ ...previousState, ...changes }));
    if (
      changes.state &&
      ['build-error', 'complete', 'progress-error'].includes(changes.state)
    ) {
      setTimeout(() => exit(), 10);
    }
  }

  async function onCancel() {
    abortController.abort();
    if (state.state === 'progress' && buildCanBeCanceled(state.build)) {
      await cancelSmokeTest(state as Required<SmokeState>);
    }
  }

  return (
    <Cancellation
      disabled={!isCancelable(state)}
      onCancel={onCancel}
      subject="smoke test"
    >
      <SmokeTestView commandUsed={commandUsed} state={state} />
    </Cancellation>
  );
}

async function smokeTestWorkflow({
  abortSignal,
  buildId,
  configPath,
  updateState,
}: {
  abortSignal: AbortSignal;
  buildId: string;
  configPath?: string;
  updateState: (changes: Partial<SmokeState>) => void;
}) {
  try {
    const {
      appId,
      build,
      contextUserId,
      nodeVersion,
      npmVersion,
      pnpmVersion,
      userId,
    } = await getBuildAttributes({
      buildId,
      configPath,
    });

    updateState({ appId, build, userId });
    validateBuild(build);

    if (!abortSignal.aborted && !isTestRunning(build)) {
      await queueSmokeTest({
        appId,
        buildId,
        nodeVersion,
        npmVersion,
        pnpmVersion,
        userId,
        contextUserId,
      });
    }

    updateState({ state: 'progress' });
    const { total } = await waitUntilFinished({
      appId,
      buildId,
      userId,
      onProgress: (progress) => updateState({ progress }),
    });

    const stateMap: Record<TotalTestProgress['state'], SmokeState['state']> = {
      done: 'complete',
      progress: 'complete', // Not actually used, just for TS
      error: 'progress-error',
      canceled: 'canceled',
    };
    updateState({ state: stateMap[total.state] });
  } catch (e) {
    const error = CliError.from(e);
    updateState({ error, state: error.type });
  }
}

function buildCanBeCanceled(build?: Build) {
  if (build?.smokeTest?.isCanceled) {
    return false;
  }

  return Boolean(build?.smokeTest?.buildServerExecutionId);
}

function isCancelable(state: SmokeState) {
  const cancelableStates: Array<SmokeState['state']> = ['loading', 'progress'];
  return cancelableStates.includes(state.state);
}
